import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from "react-leaflet";
import { useNavigate } from "react-router-dom";

const VOYAGER = `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_BASEMAPS_KEY}`;
const ATTRIB = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

function colorFor(score) {
  if (score >= 80) return "#0E9F6E";
  if (score >= 55) return "#4F46E5";
  return "#64748B";
}

function scoreIcon(color, score) {
  const size = Math.round(Math.max(28, Math.min(28 + score / 6, 44)));
  const html = `
    <div style="position:relative;width:${size}px;height:${size}px;">
      <span style="position:absolute;inset:0;border-radius:50%;background:${color};opacity:.22;transform:scale(1.7);"></span>
      <div style="position:relative;width:${size}px;height:${size}px;border-radius:50%;
        background:${color};border:2.5px solid #fff;box-shadow:0 3px 8px rgba(15,23,42,.35);
        display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;
        font-size:11px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;">${score}</div>
    </div>`;
  return L.divIcon({ html, className: "ik-marker", iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
}

export default function OpportunityMap({ opportunities }) {
  const navigate = useNavigate();

  // jitter déterministe pour que des signaux co-localisés ne se superposent pas totalement
  const seen = {};
  const points = opportunities
    .filter((item) => item.opportunity?.lat && item.opportunity?.lng)
    .map((item) => {
      const o = item.opportunity;
      const key = `${o.lat},${o.lng}`;
      const n = seen[key] || 0;
      seen[key] = n + 1;
      const angle = n * 1.2;
      const r = n === 0 ? 0 : 0.02 + n * 0.008;
      return { ...item, _lat: o.lat + Math.sin(angle) * r, _lng: o.lng + Math.cos(angle) * r };
    });

  return (
    <div className="relative rounded-xl overflow-hidden border border-border shadow-lg" data-testid="opportunity-map" style={{ height: 560 }}>
      <MapContainer center={[46.6, 2.6]} zoom={6} scrollWheelZoom style={{ height: "100%", width: "100%", background: "#e8ecf1" }}>
        <TileLayer url={VOYAGER} attribution={ATTRIB} subdomains="abcd" maxZoom={19} />
        {points.map((item) => {
          const o = item.opportunity;
          const c = colorFor(item.score);
          return (
            <Marker
              key={o.id}
              position={[item._lat, item._lng]}
              icon={scoreIcon(c, item.score)}
              eventHandlers={{ click: () => navigate(`/app/opportunity/${o.id}`) }}
            >
              <Tooltip direction="top" offset={[0, -12]} opacity={1}>
                <span style={{ fontWeight: 700 }}>{item.company.name}</span> — {item.score}/100
              </Tooltip>
              <Popup>
                <div style={{ minWidth: 190 }}>
                  <div style={{ fontFamily: "monospace", color: c, fontSize: 12, fontWeight: 700 }}>Score {item.score}/100</div>
                  <div style={{ fontWeight: 700, marginTop: 2 }}>{o.role_title}</div>
                  <div style={{ color: "#64748B", fontSize: 13 }}>{item.company.name} · {o.city}</div>
                  <button
                    onClick={() => navigate(`/app/opportunity/${o.id}`)}
                    style={{ marginTop: 10, background: "#0E9F6E", color: "#fff", border: 0, borderRadius: 8, padding: "7px 12px", fontWeight: 700, cursor: "pointer", fontSize: 12, width: "100%" }}
                  >
                    Ouvrir l'opportunité →
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Légende flottante */}
      <div className="absolute bottom-3 left-3 z-[500] bg-white/95 backdrop-blur rounded-lg shadow-md px-3 py-2 text-[11px] text-slate-700 font-medium" data-testid="map-legend">
        <div className="font-bold text-slate-900 mb-1">Score d'opportunité</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#0E9F6E" }} /> Fort (≥80)</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#4F46E5" }} /> Moyen (55–79)</div>
        <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#64748B" }} /> Faible (&lt;55)</div>
      </div>
    </div>
  );
}
